import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { Trash2} from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { purchaseReturnActions } from '../../action/purchaseReturnActions';
import { purchaseActions } from '../../action/purchaseActions';

const AddPurchaseReturn = ({ onSuccess, isOpen = false, onClose }) => {
  const modalRef = useRef(null);
  
  // Utility function to round money values to 2 decimal places
  const roundMoney = (value) => {
    if (value === null || value === undefined) return 0;
    // Round to 2 decimal places and fix floating point errors
    const rounded = Math.round((parseFloat(value) + Number.EPSILON) * 100) / 100;
    // Handle cases like 0.0000001 that should be 0
    return Math.abs(rounded) < 0.01 ? 0 : rounded;
  };

  // Format currency with proper rounding
  const formatCurrency = (amount) => {
    const rounded = roundMoney(amount);
    return `₹${rounded.toFixed(2)}`;
  };

  // Determine payment status based on rounded values
  const determinePaymentStatus = (paidAmount, grandTotal) => {
    const roundedPaid = roundMoney(paidAmount);
    const roundedTotal = roundMoney(grandTotal);
    
    if (roundedPaid >= roundedTotal) return 'paid';
    if (roundedPaid > 0) return 'partial';
    return 'unpaid';
  };
  
  // Generate unique reference number
  const generateReferenceNo = () => {
    const date = new Date();
    const year = date.getFullYear().toString().substr(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PRN${year}${month}${day}${random}`;
  };

  // Initial state
  const getInitialFormData = () => ({
    purchase_id: '',
    reference_no: generateReferenceNo(),
    return_date: dayjs().format('YYYY-MM-DD'),
    supplier_id: '',
    original_reference_no: '',
    subtotal: 0,
    tax: 0,
    discount: 0,
    shipping: 0,
    grand_total: 0,
    paid_amount: 0,
    due_amount: 0,
    payment_status: 'unpaid',
    reason: '',
    notes: '',
    status: 'pending',
    items: [],
    created_by: 'Admin'
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [supplierError, setSupplierError] = useState('');
  const [purchaseError, setPurchaseError] = useState('');

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'Return pending approval - No stock adjustment' },
    { value: 'approved', label: 'Approved', description: 'Return approved - Stock will NOT be adjusted yet' },
    { value: 'received', label: 'Received', description: 'Items received - Stock will be REDUCED from inventory' },
    { value: 'rejected', label: 'Rejected', description: 'Return rejected - No stock adjustment' },
    { value: 'completed', label: 'Completed', description: 'Return completed - Stock has been reduced' },
    { value: 'cancelled', label: 'Cancelled', description: 'Return cancelled - No stock adjustment' }
  ];

  // Reason options
  const reasonOptions = [
    { value: 'Damaged', label: 'Damaged Goods' },
    { value: 'Defective', label: 'Defective Product' },
    { value: 'Wrong Item', label: 'Wrong Item Received' },
    { value: 'Excess Supply', label: 'Excess Supply' },
    { value: 'Quality Issue', label: 'Quality Issue' },
    { value: 'Expired', label: 'Expired Product' },
    { value: 'Other', label: 'Other' }
  ];


  // Clean up modal backdrops
  const cleanupModalBackdrops = () => {
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach(backdrop => backdrop.remove());
    document.body.classList.remove('modal-open');
    document.body.style.paddingRight = '';
    document.body.style.overflow = '';
  };

  // Show Bootstrap modal
  const showBootstrapModal = () => {
    cleanupModalBackdrops();
    const modalElement = document.getElementById('add-purchase-return');
    if (modalElement) {
      if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
        window.$(modalElement).modal('show');
      } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
        let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
        if (!modalInstance) {
          modalInstance = new window.bootstrap.Modal(modalElement);
        }
        modalInstance.show();
      } else {
        modalElement.classList.add('show');
        modalElement.style.display = 'block';
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
        document.body.classList.add('modal-open');
      }
    }
  };

  // Hide Bootstrap modal
  const hideBootstrapModal = () => {
    const modalElement = document.getElementById('add-purchase-return');
    if (modalElement) {
      if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
        window.$(modalElement).modal('hide');
        setTimeout(cleanupModalBackdrops, 300);
      } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
        const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.hide();
          setTimeout(cleanupModalBackdrops, 300);
        }
      } else {
        modalElement.classList.remove('show');
        modalElement.style.display = 'none';
        cleanupModalBackdrops();
      }
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    resetForm();
    hideBootstrapModal();
    if (onClose) onClose();
    if (onSuccess) onSuccess();
  };

  // Initialize component
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => showBootstrapModal(), 50);
    }
    fetchSuppliers();
    return () => {
      cleanupModalBackdrops();
    };
  }, [isOpen]);

  // Reset form
  const resetForm = () => {
    setFormData(getInitialFormData());
    setSelectedPurchase(null);
    setSelectedSupplier(null);
    setAvailableItems([]);
    setSelectedDate(dayjs());
    setPurchases([]);
    setPurchaseLoading(false);
    setItemsLoading(false);
    setSupplierError('');
    setPurchaseError('');
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const result = await purchaseActions.getSuppliers();
      
      if (result.success) {
       
        setSuppliers(result.data || []);
      } else {
        Swal.fire('Error', result.message || 'Failed to load suppliers', 'error');
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      Swal.fire('Error', 'Failed to load suppliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load purchases for selected supplier
  const loadPurchases = async (supplierId) => {
    if (!supplierId) {
      setPurchases([]);
      setSelectedPurchase(null);
      return;
    }
    
    try {
      setPurchaseLoading(true);
      setSupplierError('');
      
      const result = await purchaseReturnActions.getPurchasesBySupplier(supplierId);
      
      if (result.success) {
        let purchasesData = result.data?.purchases || [];
        
        if (purchasesData.length > 0) {
          const formattedPurchases = purchasesData.map(purchase => ({
            value: purchase.id,
            label: `${purchase.reference_no} - ${dayjs(purchase.purchase_date).format('DD/MM/YYYY')} - ₹${roundMoney(purchase.grand_total || 0).toFixed(2)}`,
            reference_no: purchase.reference_no,
            purchase_date: purchase.purchase_date,
            grand_total: purchase.grand_total,
            payment_status: purchase.payment_status,
            status: purchase.status
          }));
          
          setPurchases([{ value: '', label: 'Select Purchase' }, ...formattedPurchases]);
          setSupplierError('');
        } else {
          setPurchases([]);
          setSupplierError('No purchases found for this supplier');
        }
      } else {
        setPurchases([]);
        setSupplierError(result.message || 'Failed to load purchases');
      }
    } catch (error) {
      console.error('Error loading purchases:', error);
      setPurchases([]);
      setSupplierError('Failed to load purchases');
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Handle supplier change
  const handleSupplierChange = (option) => {
    const supplierId = option?.value || '';
    
    setFormData(prev => ({ ...prev, supplier_id: supplierId }));
    setSelectedSupplier(option);
    setSupplierError('');
    setPurchaseError('');
    
    // Clear existing purchase data
    setSelectedPurchase(null);
    setAvailableItems([]);
    setPurchases([]);
    setFormData(prev => ({
      ...prev,
      purchase_id: '',
      original_reference_no: '',
      items: [],
      subtotal: 0,
      tax: 0,
      discount: 0,
      grand_total: 0,
      paid_amount: 0,
      due_amount: 0
    }));
    
    if (supplierId) {
      loadPurchases(supplierId);
    }
  };

  // Handle purchase selection
  const handlePurchaseChange = async (option) => {
    if (!option || !option.value) {
      setSelectedPurchase(null);
      setAvailableItems([]);
      setFormData(prev => ({
        ...prev,
        purchase_id: '',
        original_reference_no: '',
        items: [],
        subtotal: 0,
        tax: 0,
        discount: 0,
        grand_total: 0,
        paid_amount: 0,
        due_amount: 0
      }));
      return;
    }

    setSelectedPurchase(option);
    setFormData(prev => ({
      ...prev,
      purchase_id: option.value,
      original_reference_no: option.reference_no || ''
    }));

    try {
      setItemsLoading(true);
      setPurchaseError('');
      
      const result = await purchaseReturnActions.getPurchaseItems(option.value);
      
      if (result.success) {
        const itemsData = result.data?.items || [];
        
        if (itemsData.length > 0) {
          // Format available items with returnable quantity
          const formattedAvailableItems = itemsData.map(item => ({
            id: item.id || item.purchase_item_id,
            purchase_item_id: item.id || item.purchase_item_id,
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku || 'N/A',
            quantity: roundMoney(item.quantity || 0),
            unit_price: roundMoney(item.unit_price || 0),
            discount: roundMoney(item.discount || 0),
            tax_percent: roundMoney(item.tax_percent || 0),
            tax_amount: roundMoney(item.tax_amount || 0),
            total_cost: roundMoney(item.total_cost || 0),
            max_return_qty: roundMoney(item.returnable_quantity || item.quantity || 0) - roundMoney(item.returned_quantity || 0),
            already_returned: roundMoney(item.returned_quantity || 0)
          })).filter(item => item.max_return_qty > 0); // Only show items with returnable quantity
          
          setAvailableItems(formattedAvailableItems);
          setPurchaseError('');
          
          if (formattedAvailableItems.length > 0) {
            Swal.fire({
              icon: 'success',
              title: 'Items Loaded',
              text: `Found ${formattedAvailableItems.length} items available for return`,
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            setPurchaseError('No items with remaining quantity available for return');
          }
        } else {
          setAvailableItems([]);
          setPurchaseError('No items found for this purchase');
        }
      } else {
        setPurchaseError(result.message || 'Failed to load purchase items');
      }
    } catch (error) {
      console.error('Error loading purchase items:', error);
      setPurchaseError('Failed to load purchase items');
    } finally {
      setItemsLoading(false);
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setFormData(prev => ({ ...prev, return_date: date.format('YYYY-MM-DD') }));
    }
  };

  // Add item to return
  const handleAddItem = (item) => {
    const existingItem = formData.items.find(i => i.purchase_item_id === item.purchase_item_id);
    
    if (existingItem) {
      Swal.fire('Info', 'Item already added to return', 'info');
      return;
    }

    if (item.max_return_qty <= 0) {
      Swal.fire('Info', 'No quantity available for return', 'info');
      return;
    }

    const returnQty = roundMoney(Math.min(1, item.max_return_qty));
    const unitPrice = roundMoney(item.unit_price);
    const discount = roundMoney(item.discount);
    const taxPercent = roundMoney(item.tax_percent);
    const taxAmount = roundMoney((unitPrice * returnQty - discount) * (taxPercent / 100));
    const totalCost = roundMoney((unitPrice * returnQty - discount) + taxAmount);

    const newItem = {
      purchase_item_id: item.purchase_item_id,
      product_id: item.product_id,
      product_name: item.product_name,
      sku: item.sku,
      original_quantity: roundMoney(item.quantity),
      return_quantity: returnQty,
      unit_price: unitPrice,
      discount: discount,
      tax_percent: taxPercent,
      tax_amount: taxAmount,
      total_cost: totalCost,
      reason: 'Damaged' // Default reason
    };

    setFormData(prev => {
      const updatedItems = [...prev.items, newItem];
      
      // Recalculate totals
      const subtotal = roundMoney(updatedItems.reduce((sum, i) => sum + (i.unit_price * i.return_quantity), 0));
      const discountTotal = roundMoney(updatedItems.reduce((sum, i) => sum + i.discount, 0));
      const tax = roundMoney(updatedItems.reduce((sum, i) => sum + i.tax_amount, 0));
      const grandTotal = roundMoney(subtotal - discountTotal + tax + roundMoney(prev.shipping || 0));
      
      // Calculate due amount
      const paidAmount = roundMoney(prev.paid_amount || 0);
      const dueAmount = roundMoney(grandTotal - paidAmount);

      return {
        ...prev,
        items: updatedItems,
        subtotal,
        discount: discountTotal,
        tax,
        grand_total: grandTotal,
        due_amount: dueAmount,
        payment_status: determinePaymentStatus(paidAmount, grandTotal)
      };
    });
  };

  // Handle quantity change
  const handleQuantityChange = (index, value) => {
    const qty = roundMoney(value);
    
    setFormData(prev => {
      const updatedItems = [...prev.items];
      const item = updatedItems[index];
      
      const availableItem = availableItems.find(avail => 
        avail.purchase_item_id === item.purchase_item_id
      );
      
      const maxQty = roundMoney(availableItem?.max_return_qty || item.original_quantity || 0);
      const actualQty = roundMoney(Math.max(0, Math.min(qty, maxQty)));
      
      const taxAmount = roundMoney((item.unit_price * actualQty - item.discount) * (item.tax_percent / 100));
      const totalCost = roundMoney((item.unit_price * actualQty - item.discount) + taxAmount);
      
      updatedItems[index] = {
        ...item,
        return_quantity: actualQty,
        tax_amount: taxAmount,
        total_cost: totalCost
      };
      
      const subtotal = roundMoney(updatedItems.reduce((sum, i) => sum + (i.unit_price * i.return_quantity), 0));
      const discount = roundMoney(updatedItems.reduce((sum, i) => sum + i.discount, 0));
      const tax = roundMoney(updatedItems.reduce((sum, i) => sum + i.tax_amount, 0));
      const shipping = roundMoney(prev.shipping);
      const grandTotal = roundMoney(subtotal - discount + tax + shipping);
      
      const paidAmount = roundMoney(prev.paid_amount);
      const dueAmount = roundMoney(grandTotal - paidAmount);
      
      return {
        ...prev,
        items: updatedItems,
        subtotal,
        discount,
        tax,
        grand_total: grandTotal,
        due_amount: dueAmount,
        payment_status: determinePaymentStatus(paidAmount, grandTotal)
      };
    });
  };

  // Handle reason change
  const handleReasonChange = (index, option) => {
    const reason = option?.value || '';
    setFormData(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = { ...updatedItems[index], reason };
      return { ...prev, items: updatedItems };
    });
  };

  // Remove item
  const handleRemoveItem = (index) => {
    Swal.fire({
      title: 'Remove Item?',
      text: 'Are you sure you want to remove this item from return?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => {
          const updatedItems = prev.items.filter((_, i) => i !== index);
          
          const subtotal = roundMoney(updatedItems.reduce((sum, i) => sum + (i.unit_price * i.return_quantity), 0));
          const discount = roundMoney(updatedItems.reduce((sum, i) => sum + i.discount, 0));
          const tax = roundMoney(updatedItems.reduce((sum, i) => sum + i.tax_amount, 0));
          const shipping = roundMoney(prev.shipping);
          const grandTotal = roundMoney(subtotal - discount + tax + shipping);
          
          const paidAmount = roundMoney(prev.paid_amount);
          const dueAmount = roundMoney(grandTotal - paidAmount);
          
          return {
            ...prev,
            items: updatedItems,
            subtotal,
            discount,
            tax,
            grand_total: grandTotal,
            due_amount: dueAmount,
            payment_status: determinePaymentStatus(paidAmount, grandTotal)
          };
        });
        
        Swal.fire('Removed!', 'Item removed from return.', 'success');
      }
    });
  };

  // Handle shipping change
  const handleShippingChange = (value) => {
    const shipping = roundMoney(value);
    setFormData(prev => {
      const grandTotal = roundMoney(prev.subtotal - prev.discount + prev.tax + shipping);
      const paidAmount = roundMoney(prev.paid_amount);
      const dueAmount = roundMoney(grandTotal - paidAmount);
      
      return {
        ...prev,
        shipping,
        grand_total: grandTotal,
        due_amount: dueAmount,
        payment_status: determinePaymentStatus(paidAmount, grandTotal)
      };
    });
  };

  // Handle paid amount change
  const handlePaidAmountChange = (value) => {
    const paidAmount = roundMoney(value);
    setFormData(prev => {
      const grandTotal = roundMoney(prev.grand_total);
      const dueAmount = roundMoney(grandTotal - paidAmount);
      
      return {
        ...prev,
        paid_amount: paidAmount,
        due_amount: dueAmount,
        payment_status: determinePaymentStatus(paidAmount, grandTotal)
      };
    });
  };

  // Handle status change
  const handleStatusChange = (option) => {
    const newStatus = option?.value || 'pending';
    setFormData(prev => ({ ...prev, status: newStatus }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.supplier_id) {
      Swal.fire('Error', 'Please select a supplier', 'error');
      return false;
    }
    
    if (!formData.purchase_id) {
      Swal.fire('Error', 'Please select a purchase to return', 'error');
      return false;
    }
    
    if (!formData.reference_no.trim()) {
      Swal.fire('Error', 'Please enter reference number', 'error');
      return false;
    }
    
    if (!formData.return_date) {
      Swal.fire('Error', 'Please select return date', 'error');
      return false;
    }
    
    if (formData.items.length === 0) {
      Swal.fire('Error', 'Please add at least one item to return', 'error');
      return false;
    }
    
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      
      if (!item.product_name) {
        Swal.fire('Error', `Product name is required for item #${i + 1}`, 'error');
        return false;
      }
      
      if (roundMoney(item.return_quantity) <= 0) {
        Swal.fire('Error', `Valid return quantity is required for ${item.product_name}`, 'error');
        return false;
      }
      
      if (!item.reason) {
        Swal.fire('Error', `Please select a reason for ${item.product_name}`, 'error');
        return false;
      }
    }
    
    if (!formData.reason) {
      Swal.fire('Error', 'Please select a main return reason', 'error');
      return false;
    }
    
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const grandTotal = roundMoney(formData.grand_total);
      const paidAmount = roundMoney(formData.paid_amount);
      const dueAmount = roundMoney(grandTotal - paidAmount);
      const paymentStatus = determinePaymentStatus(paidAmount, grandTotal);
      
      const returnData = {
        purchase_id: formData.purchase_id,
        reference_no: formData.reference_no,
        return_date: formData.return_date,
        supplier_id: formData.supplier_id,
        original_reference_no: formData.original_reference_no,
        subtotal: roundMoney(formData.subtotal),
        tax: roundMoney(formData.tax),
        discount: roundMoney(formData.discount),
        shipping: roundMoney(formData.shipping),
        grand_total: grandTotal,
        paid_amount: paidAmount,
        due_amount: dueAmount,
        payment_status: paymentStatus,
        reason: formData.reason,
        notes: formData.notes,
        status: formData.status,
        created_by: 'Admin',
        items: formData.items.map(item => ({
          purchase_item_id: item.purchase_item_id,
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku,
          quantity: roundMoney(item.return_quantity),
          unit_price: roundMoney(item.unit_price),
          discount: roundMoney(item.discount),
          tax_percent: roundMoney(item.tax_percent),
          tax_amount: roundMoney(item.tax_amount),
          total_cost: roundMoney(item.total_cost),
          reason: item.reason
        }))
      };
      
      const result = await purchaseReturnActions.createReturn(returnData);
      
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          html: `
            <div class="text-start">
              <p>Purchase Return <strong>${formData.reference_no}</strong> created successfully.</p>
              <p><strong>Details:</strong></p>
              <ul>
                <li>Supplier: ${selectedSupplier?.label || 'N/A'}</li>
                <li>Original Purchase: ${formData.original_reference_no}</li>
                <li>Items Returned: ${formData.items.length}</li>
                <li>Total Amount: ₹${grandTotal.toFixed(2)}</li>
                <li>Paid Amount: ₹${paidAmount.toFixed(2)}</li>
                <li>Due Amount: ₹${dueAmount.toFixed(2)}</li>
                <li>Status: ${formData.status}</li>
                <li>Payment Status: ${paymentStatus}</li>
              </ul>
            </div>
          `,
          timer: 5000
        });
        
        resetForm();
        handleModalClose();
        if (onSuccess) onSuccess();
      } else {
        Swal.fire('Error', result.message || 'Failed to create purchase return', 'error');
      }
    } catch (error) {
      console.error('Error creating purchase return:', error);
      Swal.fire('Error', 'Failed to create purchase return', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (formData.items.length > 0 || formData.supplier_id || formData.purchase_id) {
      Swal.fire({
        title: 'Discard Changes?',
        text: 'You have unsaved changes. Are you sure you want to cancel?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, discard'
      }).then((result) => {
        if (result.isConfirmed) {
          resetForm();
          handleModalClose();
        }
      });
    } else {
      resetForm();
      handleModalClose();
    }
  };

  // Prepare display values
  const displayGrandTotal = roundMoney(formData.grand_total);
  const displayPaidAmount = roundMoney(formData.paid_amount);
  const displayDueAmount = roundMoney(formData.due_amount);
  const displayPaymentStatus = determinePaymentStatus(displayPaidAmount, displayGrandTotal);

  return (
    <div className="modal fade" id="add-purchase-return" ref={modalRef} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Purchase Return</h5>
            <button type="button" className="btn-close" onClick={handleCancel} aria-label="Close"></button>
          </div>
          
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Supplier <span className="text-danger">*</span></label>
                  <Select
                    options={suppliers}
                    value={selectedSupplier}
                    onChange={handleSupplierChange}
                    placeholder="Select Supplier"
                    isClearable
                    isLoading={loading}
                    isDisabled={loading}
                  />
                </div>
                
                <div className="col-md-4">
                  <label className="form-label fw-bold">Return Date <span className="text-danger">*</span></label>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="DD-MM-YYYY"
                    className="form-control"
                    style={{ width: '100%', height: '38px' }}
                    placeholder="Select Date"
                  />
                </div>
                
                <div className="col-md-4">
                  <label className="form-label fw-bold">Reference No. <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.reference_no}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference_no: e.target.value }))}
                    readOnly
                  />
                  <small className="text-muted">Auto-generated</small>
                </div>
              </div>

              {/* Purchase Selection */}
              {formData.supplier_id && (
                <div className="row g-3 mb-4">
                  <div className="col-md-8">
                    <label className="form-label fw-bold">Select Purchase to Return <span className="text-danger">*</span></label>
                    <Select
                      options={purchases}
                      value={selectedPurchase}
                      onChange={handlePurchaseChange}
                      placeholder={purchaseLoading ? "Loading purchases..." : "Select Purchase"}
                      isLoading={purchaseLoading}
                      isDisabled={purchaseLoading}
                      noOptionsMessage={() => supplierError || "No purchases found"}
                    />
                    {supplierError && <small className="text-danger d-block mt-1">{supplierError}</small>}
                  </div>
                  
                  {selectedPurchase && (
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Original Reference</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={selectedPurchase.reference_no || ''}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Loading Indicator for Items */}
              {itemsLoading && (
                <div className="alert alert-info mb-4">
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading purchase items...</span>
                  </div>
                </div>
              )}

              {/* Purchase Error */}
              {purchaseError && !itemsLoading && (
                <div className="alert alert-danger mb-4">
                  {purchaseError}
                </div>
              )}

              {/* Available Items Section */}
              {availableItems.length > 0 && !itemsLoading && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Available Items</h6>
                  <div className="table-responsive border rounded">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th className="text-end">Available Qty</th>
                          <th className="text-end">Unit Price</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableItems.map((item, index) => {
                          const isAdded = formData.items.some(i => i.purchase_item_id === item.purchase_item_id);
                          return (
                            <tr key={index}>
                              <td>{item.product_name}</td>
                              <td>{item.sku}</td>
                              <td className="text-end">{item.max_return_qty}</td>
                              <td className="text-end">{formatCurrency(item.unit_price)}</td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className={`btn btn-sm ${isAdded ? 'btn-secondary' : 'btn-primary'}`}
                                  onClick={() => handleAddItem(item)}
                                  disabled={isAdded || item.max_return_qty <= 0}
                                >
                                  {isAdded ? 'Added' : 'Add'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Return Items Table */}
              {formData.items.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Return Items ({formData.items.length})</h6>
                  <div className="table-responsive border rounded">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="20%">Product</th>
                          <th width="8%">SKU</th>
                          <th width="8%" className="text-end">Original</th>
                          <th width="8%" className="text-end">Return</th>
                          <th width="8%" className="text-end">Price</th>
                          <th width="8%" className="text-end">Total</th>
                          <th width="15%">Reason</th>
                          <th width="5%">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          const availableItem = availableItems.find(avail => 
                            avail.purchase_item_id === item.purchase_item_id
                          );
                          const maxQty = roundMoney(availableItem?.max_return_qty || item.original_quantity);
                          
                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{item.product_name}</td>
                              <td>{item.sku}</td>
                              <td className="text-end">{roundMoney(item.original_quantity)}</td>
                              <td className="text-end" style={{ width: '100px' }}>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end"
                                  value={item.return_quantity}
                                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                                  min="0"
                                  max={maxQty}
                                  step="0.01"
                                />
                                <small className="text-muted">Max: {maxQty}</small>
                              </td>
                              <td className="text-end">{formatCurrency(item.unit_price)}</td>
                              <td className="text-end fw-bold">{formatCurrency(item.total_cost)}</td>
                              <td>
                                <Select
                                  options={reasonOptions}
                                  value={reasonOptions.find(opt => opt.value === item.reason)}
                                  onChange={(option) => handleReasonChange(index, option)}
                                  className="select-sm"
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: '30px',
                                      fontSize: '13px'
                                    })
                                  }}
                                />
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveItem(index)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Summary Section */}
              {formData.items.length > 0 && (
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0">Return Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">Shipping Charges</label>
                          <div className="input-group">
                            <span className="input-group-text">₹</span>
                            <input
                              type="number"
                              className="form-control"
                              value={formData.shipping}
                              onChange={(e) => handleShippingChange(e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label">Main Return Reason <span className="text-danger">*</span></label>
                          <Select
                            options={reasonOptions}
                            value={reasonOptions.find(opt => opt.value === formData.reason)}
                            onChange={(option) => setFormData(prev => ({ ...prev, reason: option?.value || '' }))}
                            placeholder="Select main reason"
                          />
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === formData.status)}
                            onChange={handleStatusChange}
                          />
                          <small className="text-muted d-block mt-1">
                            {statusOptions.find(opt => opt.value === formData.status)?.description}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0">Payment Summary</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-sm table-borderless mb-0">
                          <tbody>
                            <tr>
                              <td>Subtotal:</td>
                              <td className="text-end">{formatCurrency(formData.subtotal)}</td>
                            </tr>
                            <tr>
                              <td>Discount:</td>
                              <td className="text-end">{formatCurrency(formData.discount)}</td>
                            </tr>
                            <tr>
                              <td>Tax:</td>
                              <td className="text-end">{formatCurrency(formData.tax)}</td>
                            </tr>
                            <tr>
                              <td>Shipping:</td>
                              <td className="text-end">{formatCurrency(formData.shipping)}</td>
                            </tr>
                            <tr className="border-top">
                              <td className="fw-bold">Grand Total:</td>
                              <td className="text-end fw-bold text-primary">{formatCurrency(displayGrandTotal)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Paid Amount:</td>
                              <td className="text-end">
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text">₹</span>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    value={displayPaidAmount}
                                    onChange={(e) => handlePaidAmountChange(e.target.value)}
                                    min="0"
                                    max={displayGrandTotal}
                                    step="0.01"
                                  />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Due Amount:</td>
                              <td className={`text-end fw-bold ${displayDueAmount > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatCurrency(displayDueAmount)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Payment Status:</td>
                              <td className="text-end">
                                <span className={`badge ${
                                  displayPaymentStatus === 'paid' ? 'bg-success' :
                                  displayPaymentStatus === 'partial' ? 'bg-warning' : 'bg-danger'
                                }`}>
                                  {displayPaymentStatus.toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-12">
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional notes about this return..."
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="row mt-4">
                <div className="col-12 text-end">
                  <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || formData.items.length === 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Processing...
                      </>
                    ) : (
                      'Create Return'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

AddPurchaseReturn.propTypes = {
  onSuccess: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default AddPurchaseReturn;